import { EOIcon } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import { validateImportRow } from '@nx/domain';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { participantImportRequested } from '../../store/slices/participantsSlice';

interface PreviewRow {
  row: number;
  name: string;
  email: string;
  company: string;
  group: string;
  ok: boolean;
  err?: string;
}

function parseCsv(text: string): PreviewRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  const rows: PreviewRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(',').map((p) => p.trim().replace(/^"|"$/g, ''));
    rows.push({
      row: i,
      name: parts[0] ?? '',
      email: parts[1] ?? '',
      company: parts[2] ?? '',
      group: parts[3] ?? 'General',
      ok: true,
    });
  }
  return rows;
}

export function ParticipantImportPage() {
  const { t } = useI18n();
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const existing = useAppSelector((s) => s.participants.items);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const emails = useMemo(() => new Set(existing.map((p) => p.email.toLowerCase())), [existing]);

  const validated = useMemo(() => {
    const seen = new Set(emails);
    return rows.map((r) => {
      const result = validateImportRow(
        { name: r.name, email: r.email, company: r.company, group: r.group },
        seen,
      );
      if (result.ok) seen.add(r.email.toLowerCase());
      return { ...r, ok: result.ok, err: result.err };
    });
  }, [rows, emails]);

  const stats = useMemo(() => {
    const ok = validated.filter((r) => r.ok).length;
    return { total: validated.length, ok, err: validated.length - ok };
  }, [validated]);

  async function onFile(file: File) {
    const text = await file.text();
    setRows(parseCsv(text));
  }

  function confirmImport() {
    const inputs = validated
      .filter((r) => r.ok)
      .map((r) => ({
        name: r.name,
        email: r.email,
        company: r.company,
        tags: [r.group || 'General'],
        status: 'pending' as const,
        source: 'csv_import' as const,
      }));
    dispatch(participantImportRequested({ eventId, inputs }));
    navigate(`/eo/events/${eventId}/participants`);
  }

  return (
    <>
      <p className="eo-bar-crumb">
        <button type="button" className="subtle-link" onClick={() => navigate(`/eo/events/${eventId}/participants`)}>
          {t('eo.nav.participants')}
        </button>
        <span className="sep">›</span>
        {t('eo.nav.import')}
      </p>

      <div className="page-head">
        <div>
          <h2>{t('eo.import.title')}</h2>
          <p>{t('eo.import.subtitle')}</p>
        </div>
      </div>

      <div
        className={`dropzone ${dragOver ? 'over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files[0];
          if (file) void onFile(file);
        }}
      >
        <div className="icon">
          <EOIcon n="upload" size={24} />
        </div>
        <strong>{t('eo.import.drop')}</strong>
        <p>CSV</p>
        <label className="btn btn-outline btn-sm" style={{ marginTop: 12 }}>
          {t('eo.form.chooseFile')}
          <input type="file" accept=".csv,text/csv" hidden onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])} />
        </label>
      </div>

      {validated.length > 0 ? (
        <>
          <div className="import-stats">
            <div>
              <span className="lbl">{t('eo.import.total')}</span>
              <span className="val">{stats.total}</span>
            </div>
            <div>
              <span className="lbl">{t('eo.import.valid')}</span>
              <span className="val ok">{stats.ok}</span>
            </div>
            <div>
              <span className="lbl">{t('eo.import.errors')}</span>
              <span className="val err">{stats.err}</span>
            </div>
          </div>
          <div className="card" style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('eo.participants.name')}</th>
                  <th>{t('eo.participants.email')}</th>
                  <th>{t('eo.participants.company')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {validated.map((r) => (
                  <tr key={r.row} className={r.ok ? '' : 'cell-err'}>
                    <td>{r.row}</td>
                    <td>{r.name}</td>
                    <td>{r.email}</td>
                    <td>{r.company}</td>
                    <td>{r.ok ? '✓' : r.err}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button type="button" className="btn btn-ghost" onClick={() => navigate(`/eo/events/${eventId}/participants`)}>
              {t('eo.import.back')}
            </button>
            <button type="button" className="btn btn-primary" disabled={stats.ok === 0} onClick={confirmImport}>
              {t('eo.import.confirm')}
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}
